'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../model/auth.store';
import { useAuthActions, useLoginLoading } from '../model/auth.selectors';
import { resolvePostLoginDestination } from '../lib/post-login-destination';
import { isSafeRedirectPath } from '../lib/safe-redirect';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { User } from '@/types/user';
import { Input } from '@/shared/ui/shadcn/input';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { ApiClientError } from '@/shared/lib/api';
import { createLogger } from '@/shared/lib/logger';
import { Loader2 } from 'lucide-react';
import { Logo } from '@/shared/ui/brand/Logo';
import { getAppleOAuthUrl } from '../lib/apple-oauth';
import { AppleLogoIcon, GoogleLogoIcon } from './social-icons';

const logger = createLogger('LoginForm');

const LOGIN_EMAIL_KEY = 'login_email';

function createLoginSchema(tValid: ReturnType<typeof useTranslations>) {
  return z.object({
    email: z
      .string()
      .min(1, tValid('required'))
      .email(tValid('email')),
    password: z.string().optional(),
  });
}

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

export function LoginForm() {
  const t = useTranslations('auth.login');
  const tValid = useTranslations('common.errors.validation');
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentLocale = useLocale();
  const { login, getGoogleOAuthUrl } = useAuthActions();
  const isLoggingIn = useLoginLoading();
  const queryClient = useQueryClient();
  const [authMethods, setAuthMethods] = useState<string[]>([]);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [isEmailFormExpanded, setIsEmailFormExpanded] = useState(false);

  const loginSchema = useMemo(() => createLoginSchema(tValid), [tValid]);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const email = watch('email') ?? '';

  // Get redirect URL from search params (validated to prevent open redirect)
  const redirectUrl = isSafeRedirectPath(searchParams.get('redirect'))
    ? searchParams.get('redirect')
    : null;

  // Load saved email on mount (from sessionStorage for security)
  useEffect(() => {
    const savedEmail = sessionStorage.getItem(LOGIN_EMAIL_KEY);
    if (savedEmail) {
      setValue('email', savedEmail);
    }
  }, [setValue]);

  // Check if there's an email parameter and logout if current user doesn't match
  useEffect(() => {
    const checkAndLogoutIfNeeded = async () => {
      logger.debug('========================================');
      logger.debug('useEffect triggered - checking auth state');

      const emailParam = searchParams.get('email');
      logger.debug('Email param from URL:', emailParam);

      if (!emailParam) {
        logger.debug('No email param, skipping check');
        return;
      }

      // Import AuthTokenService dynamically to avoid circular dependencies
      const { AuthTokenService } =
        await import('@/shared/lib/auth-token-service');
      const { logout } = useAuthStore.getState();

      // Get current user
      logger.debug('Fetching current user...');
      const currentUser = await AuthTokenService.getUser();

      logger.debug('Email param detected:', emailParam);
      logger.debug('Current user:', currentUser?.email);
      logger.debug('Auth token exists:', !!(await AuthTokenService.getToken()));

      // If user is logged in but email doesn't match, logout
      if (currentUser && currentUser.email !== emailParam) {
        logger.info('Email mismatch detected - logging out current user');
        logger.info('Current user email:', currentUser.email);
        logger.info('Target email:', emailParam);

        // Logout
        logger.debug('Calling logout...');
        await logout();
        logger.info('Logout completed');

        // Set the new email in the form
        setValue('email', emailParam);

        // Show a message to the user
        setError('root', {
          message: t('loggedOutMessage', {
            previousEmail: currentUser.email,
            targetEmail: emailParam,
          }),
        });

        logger.debug('Email pre-filled and error message set');
      } else if (!currentUser) {
        // No user logged in, just pre-fill the email
        logger.info('No user logged in, pre-filling email field');
        setValue('email', emailParam);
      } else {
        // Same user - just stay logged in
        logger.info('Same user already logged in, no action needed');
      }

      logger.debug('Check completed');
      logger.debug('========================================');
    };

    checkAndLogoutIfNeeded();
  }, [searchParams, setValue, setError, t]);

  // Handle OAuth errors separately
  useEffect(() => {
    // Check for error parameter from OAuth redirect
    const oauthError = searchParams.get('error');
    const oauthEmail = searchParams.get('email');

    if (oauthError) {
      setError('root', { message: oauthError });

      // Pre-fill email if provided
      if (oauthEmail && !email) {
        setValue('email', oauthEmail);
      }

      // Clear the error and email from URL without affecting browser history
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      url.searchParams.delete('email');
      router.replace(url.pathname, { scroll: false });
    }
  }, [searchParams, router, setError, setValue, email]);

  // Save only email when it changes (not password for security)
  useEffect(() => {
    if (email) {
      sessionStorage.setItem(LOGIN_EMAIL_KEY, email);
    }
  }, [email]);

  const onSubmit = async (data: LoginFormData) => {
    clearErrors('root');

    try {
      await login({
        email: data.email,
        password: data.password ?? '',
        locale: currentLocale,
      });
      // Clear saved email only on successful login
      sessionStorage.removeItem(LOGIN_EMAIL_KEY);
      // Redirect based on verification status
      const { pendingEmailVerification } = useAuthStore.getState();
      const user = queryClient.getQueryData<User>(cacheKeys.user) ?? null;
      if (pendingEmailVerification) {
        router.push('/auth/verify-email');
      } else {
        const destination = await resolvePostLoginDestination(user, {
          queryClient,
          redirectUrl,
        });
        router.push(destination);
      }
    } catch (error) {
      if (error instanceof ApiClientError) {
        // Extract field-specific errors
        const fieldErrors = error.getFieldErrors();
        const keys = Object.keys(fieldErrors);
        if (keys.length > 0) {
          for (const key of keys) {
            if (key === 'email' || key === 'password') {
              setError(key, { message: fieldErrors[key] });
            }
          }
          if (!keys.includes('email') && !keys.includes('password')) {
            const firstKey = keys[0];
            setError('root', {
              message:
                (firstKey ? fieldErrors[firstKey] : undefined) ??
                error.getFirstError(),
            });
          }
        } else {
          setError('root', { message: error.getFirstError() });
        }
      } else if (error instanceof Error) {
        setError('root', { message: error.message });
      } else {
        setError('root', { message: String(error) });
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Store redirect URL in sessionStorage for OAuth callback to use
      if (redirectUrl) {
        sessionStorage.setItem('oauth_redirect', redirectUrl);
      }
      const oauthUrl = await getGoogleOAuthUrl();
      window.location.href = oauthUrl;
    } catch (error) {
      console.error('Google OAuth error:', error);
      setError('root', {
        message:
          error instanceof ApiClientError
            ? error.getFirstError()
            : error instanceof Error
              ? error.message
              : String(error),
      });
    }
  };

  const handleAppleLogin = () => {
    try {
      if (redirectUrl) {
        sessionStorage.setItem('oauth_redirect', redirectUrl);
      }
      window.location.href = getAppleOAuthUrl();
    } catch (error) {
      console.error('Apple OAuth error:', error);
      setError('root', {
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  // Check auth methods when email loses focus
  const checkAuthMethods = useCallback(async () => {
    if (!email || !email.includes('@')) return;

    setIsCheckingAuth(true);
    // Don't reset hasCheckedAuth to avoid flickering

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/v1/auth/check-methods`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
          },
          body: JSON.stringify({
            data: {
              type: 'auth-check',
              attributes: {
                email: email.toLowerCase(),
              },
            },
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const methods = data.data?.attributes?.methods || [];
        setAuthMethods(methods);
        setHasCheckedAuth(true);

        // If user only has OAuth, clear password field
        if (methods.length > 0 && !methods.includes('password')) {
          setValue('password', '');
        }
      }
    } catch (error) {
      console.error('Error checking auth methods:', error);
      // Fail silently, show both options
      setAuthMethods([]);
    } finally {
      setIsCheckingAuth(false);
    }
  }, [email, setValue]);

  const handleEmailBlur = () => {
    checkAuthMethods();
  };

  // Helper to determine what auth UI to show
  // Show password field by default or if auth check hasn't completed, to avoid flickering
  const showPasswordField =
    !hasCheckedAuth ||
    authMethods.length === 0 ||
    authMethods.includes('password');
  const isOAuthOnly =
    hasCheckedAuth &&
    authMethods.length > 0 &&
    !authMethods.includes('password');
  const hasGoogleMethod = authMethods.includes('google');
  const hasAppleMethod = authMethods.includes('apple');

  // Auto-expand email form when there's a general error or pre-filled email
  // so users see the context they need to act on (e.g. OAuth failure, logout notice).
  useEffect(() => {
    if (errors.root?.message && !isEmailFormExpanded) {
      setIsEmailFormExpanded(true);
    }
  }, [errors.root, isEmailFormExpanded]);

  const generalError = errors.root?.message;

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex justify-center mb-8">
        <span className="block dark:hidden">
          <Logo variant="white" />
        </span>
        <span className="hidden dark:block">
          <Logo variant="bright" />
        </span>
      </div>
      <div className="w-full space-y-4">
          {generalError && (
            <NoticeBanner variant="error" message={generalError} />
          )}

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              className="w-full h-12 rounded-full bg-white text-[#1F1F1F] shadow-none hover:bg-white hover:opacity-90 dark:bg-black dark:text-white dark:hover:bg-black"
              onClick={handleAppleLogin}
            >
              <AppleLogoIcon />
              {t('appleButton')}
            </Button>
            <Button
              type="button"
              className="w-full h-12 rounded-full bg-white text-[#1F1F1F] shadow-none hover:bg-white hover:opacity-90 dark:bg-[#131314] dark:text-white dark:hover:bg-[#131314]"
              onClick={handleGoogleLogin}
            >
              <GoogleLogoIcon />
              {t('googleButton')}
            </Button>
          </div>

          {/* "Use email instead" link — one-way: shown only until first click, then hidden to save space. */}
          {!isEmailFormExpanded && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setIsEmailFormExpanded(true)}
                className="text-list-item-subtitle text-primary-foreground/90 underline underline-offset-2 hover:text-primary-foreground transition-colors"
                aria-expanded={isEmailFormExpanded}
                aria-controls="email-login-form"
              >
                {t('useEmailInstead')}
              </button>
            </div>
          )}

          <div
            id="email-login-form"
            className={`grid transition-all duration-300 ease-in-out ${
              isEmailFormExpanded
                ? 'grid-rows-[1fr] opacity-100'
                : 'grid-rows-[0fr] opacity-0'
            }`}
            aria-hidden={!isEmailFormExpanded}
          >
            <div className="overflow-hidden">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-primary-foreground/30" />
                  </div>
                  <div className="relative flex justify-center text-section-eyebrow">
                    <span className="bg-primary px-2 text-primary-foreground">
                      {t('continueWithEmail')}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-primary-foreground">
                    {t('email')}
                    {isCheckingAuth && (
                      <span className="ml-2 text-card-meta text-primary-foreground/80">
                        <Loader2 className="inline h-3 w-3 animate-spin" />
                      </span>
                    )}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    {...register('email', { onBlur: handleEmailBlur })}
                    className={errors.email ? 'border-destructive' : ''}
                    tabIndex={isEmailFormExpanded ? 0 : -1}
                  />
                  {errors.email && (
                    <p className="text-form-error text-destructive">{errors.email.message}</p>
                  )}
                </div>

                {isOAuthOnly && (
                  <NoticeBanner variant="info" message={t('oauthOnlyMessage')} />
                )}

                {showPasswordField && (
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-primary-foreground">{t('password')}</Label>
                    <PasswordInput
                      id="password"
                      autoComplete="current-password"
                      {...register('password')}
                      className={errors.password ? 'border-destructive' : ''}
                      tabIndex={isEmailFormExpanded ? 0 : -1}
                    />
                    {errors.password && (
                      <p className="text-form-error text-destructive">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                )}

                {showPasswordField && (
                  <div className="flex justify-end">
                    <Link
                      href="/auth/forgot-password"
                      className="text-list-item-value text-primary-foreground hover:text-primary-foreground/80"
                      tabIndex={isEmailFormExpanded ? 0 : -1}
                    >
                      {t('forgotPassword')}
                    </Link>
                  </div>
                )}

                {!isOAuthOnly && (
                  <Button
                    type="submit"
                    disabled={isLoggingIn}
                    className="w-full h-12 rounded-full bg-primary-foreground text-primary hover:bg-primary-foreground/90 dark:bg-[#0AC2B9] dark:text-[#1C2226] dark:hover:bg-[#0AC2B9]/90"
                    tabIndex={isEmailFormExpanded ? 0 : -1}
                  >
                    {isLoggingIn ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('signingIn')}
                      </>
                    ) : (
                      t('submit')
                    )}
                  </Button>
                )}

                {isOAuthOnly && hasAppleMethod && (
                  <Button
                    type="button"
                    onClick={handleAppleLogin}
                    className="w-full rounded-full bg-white text-[#1F1F1F] shadow-none hover:bg-white hover:opacity-90 dark:bg-black dark:text-white dark:hover:bg-black"
                    size="lg"
                    tabIndex={isEmailFormExpanded ? 0 : -1}
                  >
                    <AppleLogoIcon />
                    {t('oauthOnlyAppleButton')}
                  </Button>
                )}
                {isOAuthOnly && hasGoogleMethod && (
                  <Button
                    type="button"
                    onClick={handleGoogleLogin}
                    className="w-full rounded-full bg-white text-[#1F1F1F] shadow-none hover:bg-white hover:opacity-90 dark:bg-[#131314] dark:text-white dark:hover:bg-[#131314]"
                    size="lg"
                    tabIndex={isEmailFormExpanded ? 0 : -1}
                  >
                    <GoogleLogoIcon />
                    {t('oauthOnlyButton')}
                  </Button>
                )}

                <div className="text-center text-list-item-subtitle text-primary-foreground/90">
                  {t.rich('description', {
                    createAccount: (chunks) => (
                      <Link
                        href="/auth/register"
                        className="font-medium text-primary-foreground underline hover:text-primary-foreground/80"
                        tabIndex={isEmailFormExpanded ? 0 : -1}
                      >
                        {chunks}
                      </Link>
                    ),
                  })}
                </div>
              </form>
            </div>
          </div>
      </div>
    </div>
  );
}
